import type { Observable } from 'rxjs'
import { type CommunityItem, GetCommunitiesRequest } from '@/api/rxjs/knst/ISPForm/models/get_communities.model'
import type { AddOnItem } from '@/api/rxjs/knst/ISPForm/models/get_addon_items.model'
import type { GetPlansResponse, ISPPlanType, ISPPlanDetail } from '@/api/rxjs/knst/ISPForm/models/get_plans.model'
import type { GetRoadCommunityItem } from '@/api/rxjs/knst/ISPForm/models/get_road_community.model'
import type { PostISPFormAddOnItem } from '@/api/rxjs/knst/ISPForm/models/post_ISP_form.model'
import { ISP_SUBMIT_MODE } from '@/views/ISPInstall/pages/step/enums/ISPSubmitMode.enum'
import { SelectedPlan } from '@/views/ISPInstall/pages/step/models/selected_plan.model'
import { COMMUNITY_QUERY_RESULT } from '@/views/ISPInstall/pages/step/enums/CommunityQueryResult.enum'
import { ISPTypeForm } from '@/views/ISPInstall/pages/step/models/isp_type_form.model'
import { ISPInfoForm } from '@/views/ISPInstall/pages/step/models/isp_info_form.model'

export const useIspInstallStore = defineStore('ispInstall', {
  state: () => ({
    bannerToggler: ref<boolean>(true),
    pageLoading: ref<boolean>(false),
    /** 用戶前往申裝來源 */
    userSource: ref<number>(0),
    searchNPick_queryForm: ref<GetCommunitiesRequest>(new GetCommunitiesRequest()),
    searchNPick_communityList: ref<CommunityItem[]>([]),
    searchNPick_userInputForm: ref<{ communityName: string }>({ communityName: '' }),
    searchNPick_queryStatus: ref<COMMUNITY_QUERY_RESULT>(COMMUNITY_QUERY_RESULT.UNSEARCH),
    searchNPick_ispForm: ref<ISPTypeForm>(new ISPTypeForm()),
    /** 行政區下拉清單（持久化，回上一步保留選項）*/
    searchNPick_districtOptions: ref<string[]>([]),
    /** 路名下拉清單（持久化）*/
    searchNPick_roadOptions: ref<string[]>([]),
    /** 已選路名（持久化）*/
    searchNPick_selectedRoad: ref<string>(''),
    /** 路名底下社區下拉清單（持久化）*/
    searchNPick_communityOptions: ref<GetRoadCommunityItem[]>([]),
    /** 社區下拉選中的社區名稱（持久化）*/
    searchNPick_selectedCommunityName: ref<string>(''),
    selectedPlan: ref<SelectedPlan>(new SelectedPlan()),
    /** 已選用的社區專屬團購方案 id（持久化；null＝未選用、維持 step0 方案，供重整後還原選中高亮）*/
    groupBuy_selectedId: ref<number | null>(null),
    /** 選用團購前的 step0 原方案（持久化；取消團購時切回，重整後才有得還原）*/
    groupBuy_originalPlan: ref<{ planType: ISPPlanType | null, planDetail: ISPPlanDetail | null } | null>(null),
    selectedCommunity: ref<CommunityItem | null>(null),
    infoForm: ref<ISPInfoForm>(new ISPInfoForm()),
    planData: ref<GetPlansResponse | null>(null),
    /** step0 預設方案（§3.6.1 先選方案；is_default=1，不綁社區）*/
    defaultPlanData: ref<GetPlansResponse | null>(null),
    submitMode: ref<ISP_SUBMIT_MODE>(ISP_SUBMIT_MODE.DEFAULT),
    /**
     * Google reCAPTCHA token（空字串＝尚未通過驗證）。
     * 由填寫資訊頁／預約專人來電頁的 recaptchaBox 產生，僅作送出前的前端閘門
     * （CreateComplainAndApplyOrder 端點走 ExternalAPIKeyAuthorize，不驗證此 token）。
     * 刻意不持久化：token 具時效（約 2 分鐘）且為一次性，重整後應重新驗證。
     */
    recaptchaToken: ref<string>(''),
    /** 諮詢模式（由 broadband.html「我要諮詢」進入）：先選社區查供裝狀態，除「無法服務」外下一步都導預約專人來電、不下單 */
    consultMode: ref<boolean>(false),
    addonItemList: ref<AddOnItem[]>([]),
    addonIdPriceMap: ref<Map<number, number>>(new Map()),
    validateArray: ref<Array<() => Observable<boolean>>>([]),
    /**
     * 「下一步」已在 cartPanel.goNextStep 驗證通過、即將 router.push 時設為 true，
     * 讓 step 版面的 onBeforeRouteUpdate 略過這一次的重複驗證。
     * 避免驗證器副作用（如未選加購的確認窗）被執行兩次 → 需點兩次才前進。
     */
    skipForwardValidationOnce: ref<boolean>(false)
  }),
  actions: {
    resetInfoAddress() {
      this.infoForm.resetAddress()
    }
  },
  getters: {
    concatAddress: (state) => {
      const city = state.infoForm.address_cityName
      const district = state.infoForm.address_districtName
      const road = state.infoForm.address_roadName
      const lane = state.infoForm.address_lane
      const semiLane = state.infoForm.address_semiLane
      const alley = state.infoForm.address_alley
      const semiAlley = state.infoForm.address_semiAlley
      const number = state.infoForm.address_number
      const semiNumber = state.infoForm.address_semiNumber
      const floor = state.infoForm.address_floor
      const semiFloor = state.infoForm.address_semiFloor
      const etc = state.infoForm.address_etc

      // 巷 / 弄 / 號 的副號在約工單以「-」呈現（如 12-1巷、54-44號），非「之」
      // 主號/副號只要任一有值就輸出；以主號為條件會在主號留空時把已填的副號一併丟棄，
      // 造成約工單地址缺巷/弄/號（如「桃園市桃園區愛二街 2樓」）
      const seg = (main: string, semi: string, unit: string): string => {
        const parts = [main, semi].filter(v => v.trim() !== '')
        return parts.length ? `${parts.join('-')}${unit}` : ''
      }

      let output = `${city}${district}${road}`
      output += seg(lane, semiLane, '巷')
      output += seg(alley, semiAlley, '弄')
      output += seg(number, semiNumber, '號')
      // 樓的副號維持「之」（如 5樓之2）
      output += floor ? ` ${floor}樓${semiFloor ? `之${semiFloor}` : ''}` : ''
      output += etc ? `(${etc})` : ''

      return output
    },
    addOnItemListForPostISP: (state): PostISPFormAddOnItem[] => {
      const addOnItems = state.selectedPlan.addonItems

      return Object.keys(addOnItems).reduce<PostISPFormAddOnItem[]>(
        (prev, key) => {
          const amount = addOnItems[+key]
          // 後端 payload 只需 Id + Quantity，不該用 price 過濾（否則 price 0/查無價的加購會被丟掉）
          if (!amount || amount <= 0) return prev
          prev.push({
            Id: +key,
            Quantity: amount
          })
          return prev
        },
        []
      )
    }
  }
})